import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import GetQueryParams from "components/common/services/GetQueryParams";
import {
  createQueryParams,
  filterAndMerge,
  getQueryParamsFromRedux,
} from "components/common/utlis/HelperFunctions";
import { createFilterConfig } from "./Filter";
import { OptionProps, ValidationRules } from "components/common/utlis/TypeAnnotations";
import {
  clearDestinationPath,
  setDestinationPath,
} from "features/NavigationSlice";
import { ApiCall } from "components/common/services/ApiServices";
import { CANCEL_PLANNING, FETCH_PLANNINGS } from "../annotation/PlanningConstants";
import { COMPANY_MICROSERVICE } from "Constants";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import GetCrudPermissions from "components/common/services/RolesAndPermissions/GetCrudPermissions";
import { selectAuth } from "features/auth/AuthSlice";
import Title from "components/common/atoms/Title";
import { t } from "components/CentralDataMangement/translation/Translation";
import SearchFields from "components/common/atoms/SearchFields";
import ResetBtn from "components/common/atoms/ResetBtn";
import Sorting from "components/common/atoms/Sorting";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import { Link, useNavigate } from "react-router-dom";
import EditIcon from "static/images/EditIcon";
import NoRecords from "components/common/atoms/NoRecords";
import PaginationNew from "components/common/atoms/PaginationNew";
import LinkTo from "components/common/atoms/LinkTo";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import ModalPopup from "components/common/atoms/ModalPopup";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import CloseFile from "static/images/CloseFile";
import { validateForm, validateRequired } from "components/common/services/ValidationService";
import CustomNotify from "components/common/atoms/CustomNotify";
import CloneIcon from "static/images/CloneIcon";
import NavBar from "components/common/atoms/NavBar";
import ViewIcon from "static/images/ViewIcon";
import InfoIcon from "static/images/InfoIcon";
import ActionIconWrapper from "components/common/atoms/ActionIconWrapper";
import TextEllipsis from "components/common/atoms/TextEllipsis";
import ExportFile from "components/common/atoms/ExportFile";

interface PlanningProps {
  office: string;
  contractNumber: string;
  contractId?: string | number | null; // stored at push; number filled later by poller
  customer: string;
  companyId: number | null;
  collegue: string;
  empId: number | null;
  from: string;
  to: string;
  planningId: number;
  first_name: string;
  last_name: string;
  formStatus: number | null;
  planningStatus: string;
  is_past_date: number;
  employeeType: string;
  functionName: string;
  pcNumber: string;
  brightError?: string | null; // latest Bright validation error (from failed push)
  createdOn?: string | null; // when the contract/planning was created (ped.created_at)
  cancelReason?: string | null; // latest cancellation reason (shown when Inactive)
  cancelDate?: string | null;
  cancelBy?: string | null;
}

interface SearchProps {
  office: number[];
  contractNumber: string;
  companyName: string;
  candidateName: string;
  from: Date | null | string;
  to: Date | null | string;
  contractType: string | null;
  status: OptionProps | null;
  orderByColumn: string;
  orderIn: "" | "asc" | "desc"; // Ensure correct typing here
  currentPage: number;
  recordsPerPage: number;
  employeeType: string;
  functionName: string;
  pcNumber: string;
  isStrictSearch: boolean;
}

const ManagePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canCompany = GetCrudPermissions("Company");
  const canCandidate = GetCrudPermissions("Candidate");
  // Super admin bypasses permission checks in AccessControl but has no explicit
  // "Candidate" read row, so GetCrudPermissions returns undefined for them.
  const authUser = useSelector(selectAuth);
  const [planningData, setPlanningData] = useState<PlanningProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 0,
  });
  // Dropdown options are static; fetch them once and reuse on later searches.
  const dropdownLoadedRef = useRef(false);
  const [dropdownList, setDropdownList] = useState({
    // agents: [] as OptionProps[],
    status: [] as OptionProps[],
    offices: [] as OptionProps[],
    collegues: [] as OptionProps[],
  });
  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );

  const getUrlParams = GetQueryParams();
  const paramFilter = getUrlParams.get("filter");
  const queryParams = getQueryParamsFromRedux(destinationPath, paramFilter);

  // Old: const filters = createFilterConfig(dropdownList);  (moved below searchData)

  const defaultSearchData: SearchProps = {
    office: [],
    contractNumber: "",
    companyName: "",
    candidateName: '',
    from: "",
    to: "",
    orderByColumn: "",
    contractType: null,
    status: null,
    orderIn: "",
    currentPage: 1,
    recordsPerPage: 10,
    employeeType: "",
    functionName: "",
    pcNumber: "",
    isStrictSearch: false
  };

  //active tab status
  const defaultTabData = {
    tabFieldName: "contractType",
    tabFieldValue: null,
  };

  const filteredSearchData = filterAndMerge(defaultSearchData, queryParams);
  const filteredTabData = filterAndMerge(defaultTabData, queryParams);


  // Initialize search data based on query params, merging with default values
  const [searchData, setSearchData] = useState<SearchProps>({
    ...defaultSearchData,
    ...filteredSearchData,
  });

  const [activeTab, setActiveTab] = useState({
    ...defaultTabData,
    ...filteredTabData,
  });

  // Status filter is only available on the "All" tab (no contractType selected).
  const filters = createFilterConfig(dropdownList, !!searchData.contractType);

  const navItems = [
    { label: t("All"), fieldName: "contractType", value: null },
    { label: t("Active"), fieldName: "contractType", value: "Active" },
    { label: t("Inactive"), fieldName: "contractType", value: "Inactive" },
  ];

  // Auto-refresh: the Bright push runs in the background after creation, so
  // re-fetch quietly while any visible row is still settling (see isSettling).
  const STATUS_POLL_INTERVAL_MS = 10000;
  const STATUS_POLL_MAX_ATTEMPTS = 30; // ~5 minutes, then stop polling
  const statusPollCountRef = useRef(0);
  // Only the latest request may update the table (a slow background refresh must
  // not overwrite results of a newer search).
  const latestRequestRef = useRef(0);

  useEffect(() => {
    dispatch(clearDestinationPath());
    const filterData = { ...searchData, ...activeTab };
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));
    setLoading(true);
    statusPollCountRef.current = 0;
    const delayDebounceFn = setTimeout(() => {
      fetchPlanningDetails({ ...searchData });
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData]);

  // Not pushed yet (no status, no Bright error), or pushed and still waiting
  // for Bright's contract number.
  const isSettling = (item: PlanningProps) =>
    (!item.planningStatus && !item.brightError) ||
    (item.planningStatus === "ACTIVE" && !item.contractNumber);

  useEffect(() => {
    if (
      loading ||
      statusPollCountRef.current >= STATUS_POLL_MAX_ATTEMPTS ||
      !planningData?.some(isSettling)
    ) {
      return;
    }
    const pollTimer = setTimeout(() => {
      statusPollCountRef.current += 1;
      fetchPlanningDetails({ ...searchData }, true);
    }, STATUS_POLL_INTERVAL_MS);
    return () => clearTimeout(pollTimer);
  }, [planningData, loading, searchData]);

  // Old:
  // const fetchPlanningDetails = async (filterData: any) => {
  //   setLoading(true);
  const fetchPlanningDetails = async (filterData: any, silent = false) => {
    const requestId = ++latestRequestRef.current;
    if (!silent) {
      setLoading(true);
    }
    const data = {
      search: filterData,
      loadDropdown: !dropdownLoadedRef.current,
    };

    const response = await ApiCall.service(
      FETCH_PLANNINGS,
      "POST",
      data,
      false,
      COMPANY_MICROSERVICE
    );
    if (requestId !== latestRequestRef.current) {
      return; // superseded by a newer search/refresh
    }
    if (response?.status === 200) {
      const { apiData, dropdownData, paginationData } = response?.data;
      setPlanningData(apiData);
      setPaginationData(paginationData);
      // const usersList = dropdownData.agents.map((item: any) => ({
      //   value: item.userId,
      //   label: `${item.first_name} ${item.last_name}`,
      // }));

      if (dropdownData) {
        setDropdownList((prevData) => ({
          ...prevData,
          ...dropdownData,
          // agents: usersList,
        }));
        dropdownLoadedRef.current = true;
      }
    // Old: }else{   (error toast on every failed fetch)
    } else if (!silent) {
      CustomNotify({
        type : "error",
        message : t("Some error occured")
      });
    }
    // Old: setLoading(false);
    if (!silent) {
      setLoading(false);
    }
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      isStrictSearch: false,
      [name]: value,
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      isStrictSearch: false,
      [fieldName]: selectedOption,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleReset = async (e: React.FormEvent) => {
    dispatch(clearDestinationPath());
    setSearchData({ ...defaultSearchData, ...activeTab, office: [] });
    handleNavItemClick('contractType', null);
  };

  const handleNavItemClick = (tabFieldName: string, tabFieldValue: any) => {
    setSearchData((prev) => {
      const updatedState: any = { ...prev };
      updatedState[tabFieldName] = tabFieldValue;
      updatedState["currentPage"] = 1;
      // Status filter is disabled on Active/Inactive: drop any value picked on "All".
      if (tabFieldValue) {
        updatedState["status"] = null;
      }

      navItems.forEach((item) => {
        if (item.fieldName !== tabFieldName) {
          updatedState[item.fieldName] = null;
        }
      });

      return updatedState;
    });
    setActiveTab({ tabFieldName, tabFieldValue });
  };

  const handleOrderByColumn = (column: string) => {
    let newOrder: "" | "asc" | "desc" = ""; // Explicit type declaration
    let newColumn = column;

    if (searchData.orderByColumn === column) {
      // Cycle through the three possible order values: '', 'asc', 'desc'
      if (searchData.orderIn === "") {
        newOrder = "asc";
      } else if (searchData.orderIn === "asc") {
        newOrder = "desc";
      } else {
        newOrder = "";
        newColumn = "";
      }
    } else {
      newOrder = "asc";
    }

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      orderByColumn: newColumn,
      orderIn: newOrder,
    }));
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      [fieldName]: date,
    }));
  };

  /* Modal popup code starts */
  const modalValidation = (
    name: string,
    value: string | boolean | null,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      cancelReason: [validateRequired],
    };

    const validationErrors = validateForm(
      { ...cancelPlanning, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(cancelPlanningErrors).length > 0) {
      setCancelPlanningErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setCancelPlanningErrors({
        cancelReason: validationErrors.cancelReason || "",
      });
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    return true;
  };

  const [cancelPlanning, setCancelPlanning] = useState({
    cancelId: null as number | null,
    cancelReason: "" as string,
  });

  // Contract-info popup (opened from the info icon on every row): shows push
  // status, contract status, any Bright error, and created/updated timestamps.
  const [infoModal, setInfoModal] = useState<{ show: boolean; item: PlanningProps | null }>({
    show: false,
    item: null,
  });

  const [cancelPlanningErrors, setCancelPlanningErrors] = useState({
    cancelReason: "" as string,
  });

  const [modalDetails, setModalDetails] = useState({
    title: "" as string,
    body: "" as string,
    type: "" as string,
    showModal: false as boolean
  });

  const handleCancelPlanning = (id: number | null) => {
    setCancelPlanning((prev) => ({
      ...prev,
      cancelId: id,
    }));

    setModalDetails((prev) => ({
      ...prev,
      title: t("Cancel contract"),
      type: "cancel",
      showModal: true
    }));

  };

  const handleModalClose = () => setModalDetails((prev) => ({
    ...prev,
    showModal: false
  }));


  const handleModalSubmit = async (e: React.FormEvent) => {

    const { name, value } = e.target as HTMLInputElement;
    if (modalValidation(name, value)) {
      setModalDetails((prev) => ({
        ...prev,
        showModal: false
      }));
      if (cancelPlanning.cancelId) {
        const data = {
          ...cancelPlanning,
          type: 'ALL'
        }
        const response = await ApiCall.service(
          `${CANCEL_PLANNING}/${cancelPlanning.cancelId}`,
          "POST",
          data,
          false,
          "company-creation"
        );

        if (response?.status === 200) {
          // Refresh the list instead of removing the row — a cancelled contract should
          // stay visible (it moves to the Inactive tab), not disappear from the UI.
          fetchPlanningDetails({ ...searchData });
          CustomNotify({ type: "success", message: t(response?.msg) });
        } else {
          CustomNotify({ type: "error", message: t(response?.message) });

        }
      }
    }

    setCancelPlanning((prev) => ({
      ...prev,
      cancelId: null,
      cancelReason: ""
    }));
  };


  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;

    setCancelPlanning((prev) => ({
      ...prev,
      [name]: value
    }));
    modalValidation(name, value, true)
  }
  /* Modal popup code ends */

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Contract",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div className={`main-container rightSidebar`}>
        <Title title={t("Contracts")} />
        <div className="form-height-dashboard">
          <div className="search-bar">
            <div className="row">
              <div className="col-md-12 company-tabs">
                <NavBar
                  navItems={navItems}
                  onNavItemClick={handleNavItemClick}
                  className="custom-navbar"
                  activeItem={activeTab}
                />
              </div>
            </div>
          </div>
          <div className="position-relative tableMainWrapper">
            <div className="row">
              <div className="ManageCreateBtn">
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Contract",
                      create: true,
                    }
                  ]}
                >
                  <LinkTo
                    pagelink="/planning/create"
                    title={t("Create contract")}
                    icon={faPlus}
                    className="me-3"
                  />
                </AccessControl>
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Export Files",
                      read: true,
                    },
                  ]}
                >
                  <ExportFile
                    source="planning"
                    searchParams={searchData}
                    microService={COMPANY_MICROSERVICE}
                    className="marginRightPoint5"
                  />
                </AccessControl>
              </div>
            </div>
            <div className="table-responsive tableSection">
              <table className="table manageContractsWrapper border-0 mb-0">
                <thead>
                  <tr className="tableSearchFields">
                    <SearchFields
                      searchData={searchData}
                      filters={filters}
                      handleSearchChange={handleSearchChange}
                      handleSelectChange={handleSelectChange}
                      handleDateChange={handleDateChange}
                    />
                    <th className="border-0">
                      <div className="d-flex align-items-center w-100">
                        <ResetBtn handleResetClick={handleReset} />
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="tableContainer scrollBarDesign">
                <table className="table table-hover manageContractsWrapper">
                  <thead>
                    <tr className="TableHeader">
                      <th onClick={() => handleOrderByColumn("office")}>
                        {t("Office")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "office"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("contractNumber")}>
                        {t("Contract no.")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "contractNumber"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("company")}>
                        {t("Customer")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "company"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("candidateName")}>
                        {t("Employee")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "candidateName"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("employeeType")}>
                        {t("Employee type")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "employeeType"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("functionName")}>
                        {t("Function")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "functionName"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("pcNumber")}>
                        {t("PC")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "pcNumber"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("from")}>
                        {t("From")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "from"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th>{t("To")}</th>
                      <th>{t("Status")}</th>
                      <AccessControl
                        requiredPermissions={[
                          {
                            permission: "Contract",
                            update: true,
                            delete: true,
                          },
                        ]}
                        actions={true}
                        strict={false}
                      >
                        <th>{t("Action")}</th>
                      </AccessControl>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      // Old: <SpinnerWrapper headerLength={10} /> (before the Status column)
                      <SpinnerWrapper headerLength={11} />
                    ) : (
                      <>
                        {planningData && planningData.length > 0 ? (
                          planningData.map(
                            (item: PlanningProps, index: number) => (
                              <tr
                                key={index}
                                className="border-bottom mb-3 box-shadow align-middle"
                              >
                                <td
                                  className="text-break ps-lg-4"
                                  data-label={t("Office")}
                                >
                                  {item.office}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("contract number")}
                                >
                                  {item.contractNumber}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("Customer")}
                                >
                                  {canCompany?.read && item.companyId ? (
                                    <Link
                                      to={`/company/corner/${item.companyId}`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {item.customer}
                                    </Link>
                                  ) : (
                                    item.customer
                                  )}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("Colleague")}
                                >
                                  {/* {canCandidate?.read && item.empId ? ( */}
                                  {(authUser?.isSuperAdmin || canCandidate?.read) && item.empId ? (
                                    <Link
                                      to={`/candidate/corner/${item.empId}`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <TextEllipsis title={t(`${item.collegue}`)} label={t(`${item.collegue}`)} width="12.8vw" />
                                    </Link>
                                  ) : (
                                    <TextEllipsis title={t(`${item.collegue}`)} label={t(`${item.collegue}`)} width="12.8vw" />
                                  )}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("Employee type")}
                                >
                                  {t(`${item.employeeType}`)}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("Function name")}
                                >
                                  {t(`${item.functionName}`)}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("PC")}
                                >
                                  {t(`${item.pcNumber}`)}
                                </td>
                                <td className="text-break" data-label={t("From")}>
                                  {item.from}
                                </td>
                                <td className="text-break" data-label={t("To")}>
                                  {item.to}
                                </td>
                                {/* Active = green, Inactive (cancelled) = yellow, Failed (not pushed) = red. */}
                                <td className="text-break" data-label={t("Status")}>
                                  <span
                                    style={{ marginLeft: "1vw" }}
                                    className={`d-inline-block align-middle rounded-circle status-icon ${
                                      item.planningStatus === "INACTIVE"
                                        ? "bg-warning"
                                        : item.planningStatus
                                          ? "bg-success"
                                          : "bg-danger"
                                    }`}
                                    title={t(
                                      item.planningStatus === "INACTIVE"
                                        ? "Inactive"
                                        : item.planningStatus
                                          ? "Active"
                                          : "Failed"
                                    )}
                                  ></span>
                                </td>
                                <AccessControl
                                  requiredPermissions={[
                                    {
                                      permission: "Contract",
                                      update: true,
                                      delete: true,
                                    },
                                  ]}
                                  actions={true}
                                  strict={false}
                                >
                                  <td className="table-action-icons px-2">
                                    {/* Show Info + Edit, then the ▶ arrow expands View/Extend/Cancel. */}
                                    <ActionIconWrapper initialVisibleCount={2}>
                                    {/* Info (every row): push status, contract status, Bright error, created/updated. */}
                                    <span
                                      className="btn p-0 border-0 me-2 cursor-pointer"
                                      title={t("Info")}
                                      onClick={() => setInfoModal({ show: true, item })}
                                    >
                                      <InfoIcon />
                                    </span>
                                    {/* {(item.is_past_date != 1 && item.planningStatus != 'INACTIVE') && */}
                                    <AccessControl
                                      requiredPermissions={[
                                        {
                                          permission: "Contract",
                                          read: true,
                                        },
                                      ]}
                                    >
                                      <Link
                                        to={`/planning/edit/${item.planningId}`}
                                        className="btn p-0 border-0 me-2"
                                        title={t("Edit")}
                                      >
                                        <EditIcon />
                                      </Link>
                                    </AccessControl>
                                    {/* } */}
                                    {/* {(item.is_past_date == 1 || item.planningStatus == 'INACTIVE') && */}
                                      <AccessControl
                                        requiredPermissions={[
                                          {
                                            permission: "Contract",
                                            read: true,
                                          },
                                        ]}
                                      >
                                        <Link
                                          to={`/planning/view/${item.planningId}`}
                                          className="btn p-0 border-0 me-2"
                                          title={t("View")}
                                        >
                                          <ViewIcon />
                                        </Link>
                                      </AccessControl>
                                    {/* } */}
                                    {item.formStatus != 0 &&
                                      <>
                                        <AccessControl
                                          requiredPermissions={[
                                            {
                                              permission: "Contract",
                                              create: true,
                                            },
                                          ]}
                                        >
                                          <Link
                                            to={`/clone/planning/edit/${item.planningId}`}
                                            className="btn p-0 border-0 me-2"
                                            title={t("Extend contract")}
                                          >
                                            <CloneIcon />
                                          </Link>
                                        </AccessControl>
                                      </>
                                    }
                                    {(item.planningStatus != 'INACTIVE' && item.is_past_date != 1 && (item.formStatus !== 0 && item.planningStatus != null)) &&
                                      <>
                                        <AccessControl
                                          requiredPermissions={[
                                            {
                                              permission: "Contract",
                                              delete: true,
                                            },
                                          ]}
                                        >
                                          <Link
                                            to=''
                                            className="btn p-0 border-0 me-2"
                                            title={t("Cancel contract")}
                                            onClick={() => handleCancelPlanning(item.planningId)}
                                          >
                                            < CloseFile />
                                          </Link>
                                        </AccessControl>
                                      </>
                                    }
                                    </ActionIconWrapper>
                                  </td>
                                </AccessControl>
                              </tr>
                            )
                          )
                        ) : (
                          <NoRecords headerLength={10} />
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!loading && (
                <div className="pagination d-block">
                  <PaginationNew
                    currentPage={searchData.currentPage}
                    totalPages={paginationData.totalPages}
                    totalRecords={paginationData.totalRecords}
                    recordsPerPage={searchData.recordsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={(newPage: any) => handlePageChange(newPage)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className="col-md-6 align-self-center backPadding">
          <Link
            to=""
            className=" back-btn text-decoration-underline"
            onClick={() => navigate(-1)}
          >
            {t("Back")}
          </Link>
        </div>
        <ModalPopup
          show={infoModal.show}
          onHide={() => setInfoModal({ show: false, item: null })}
          title={t("Contract info")}
          body={
            <div className="row">
              <div className="col-md-12">
                <p className="mb-2">
                  <strong>{t("Push status")}: </strong>
                  {infoModal.item?.contractNumber
                    ? t("Pushed to Bright")
                    : infoModal.item?.brightError
                      ? t("Failed")
                      : infoModal.item?.contractId
                        ? t("Created in Bright – awaiting contract number")
                        : t("Pending")}
                </p>
                <p className="mb-2">
                  <strong>{t("Status")}: </strong>
                  {infoModal.item?.planningStatus === "INACTIVE"
                    ? t("Inactive")
                    : infoModal.item?.planningStatus
                      ? t("Active")
                      : "-"}
                </p>
                <p className="mb-2">
                  <strong>{t("Created on")}: </strong>
                  {infoModal.item?.createdOn || "-"}
                </p>
                {infoModal.item?.planningStatus === "INACTIVE" && infoModal.item?.cancelReason &&
                  <p className="mb-2 text-break">
                    <strong>{t("Cancellation")}: </strong>
                    {infoModal.item.cancelReason}
                    {infoModal.item.cancelDate ? ` · ${infoModal.item.cancelDate}` : ""}
                    {infoModal.item.cancelBy ? ` · ${t("by")} ${infoModal.item.cancelBy}` : ""}
                  </p>
                }
                {infoModal.item?.brightError &&
                  <p className="mb-0 text-break" style={{ whiteSpace: "pre-line" }}>
                    <strong>{t("Bright error")}: </strong>
                    {(infoModal.item.brightError || "")
                      .replace(/&lt;/g, "<")
                      .replace(/&gt;/g, ">")
                      .replace(/&amp;/g, "&")
                      .replace(/<br\s*\/?>/gi, "\n")
                      .replace(/<[^>]+>/g, "")
                      .trim()}
                  </p>
                }
              </div>
            </div>
          }
          onCloseButtonClick={() => setInfoModal({ show: false, item: null })}
          closeTitle={t("Close")}
        />
        <ModalPopup
          show={modalDetails.showModal}
          onHide={handleModalClose}
          title={modalDetails.title}
          body={
            <div className="row">
              <div className="col-md-12">
                <LabelWithInputField
                  handleChange={handleFieldChange}
                  label={t("You are about to cancel this contract. Reason for cancellation?")}
                  type="text"
                  id="cancelReason"
                  name="cancelReason"
                  value={cancelPlanning.cancelReason}
                  error={cancelPlanningErrors.cancelReason}
                  isMandatory={true}
                />
              </div>
            </div>}
          onCloseButtonClick={handleModalClose}
          onConfirmButtonClick={handleModalSubmit}
          closeTitle={t("No")}
          confirmTitle={t("Yes")}
        />
      </div>
    </AccessControl>
  );
};

export default ManagePlanning;
